/**
 * React Query hooks for the Relay Content module endpoints.
 */
import { useAuth } from "@clerk/clerk-react";
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  ApiError,
  BrandVoiceListResponse,
  BrandVoiceResponse,
  ContentAnalyticsResponse,
  ContentImageUploadResponse,
  ContentPostListResponse,
  ContentPostResponse,
  CreateBrandVoiceRequest,
  CreateContentPostRequest,
  FunnelResponse,
  GeneratePostRequest,
  PlatformConnectionListResponse,
  PlatformConnectionResponse,
  SchedulePostRequest,
  UpdateBrandVoiceRequest,
  UpdateContentPostRequest,
  UpsertPlatformConnectionRequest,
} from "@/lib/api-client";
import {
  createBrandVoice,
  createPost,
  deleteBrandVoice,
  deleteConnection,
  deletePost,
  fetchBrandVoices,
  fetchConnections,
  fetchContentAnalytics,
  fetchFunnel,
  fetchPost,
  fetchPosts,
  generatePost,
  publishPost,
  retryPost,
  schedulePost,
  updateBrandVoice,
  updatePost,
  uploadContentImage,
  upsertConnection,
} from "@/lib/api-client";

export const contentKeys = {
  all: ["content"] as const,
  voices: () => ["content", "brand-voices"] as const,
  posts: (params: { status?: string; platform?: string } = {}) =>
    ["content", "posts", params] as const,
  post: (id: string) => ["content", "posts", id] as const,
  connections: () => ["content", "connections"] as const,
  analytics: () => ["content", "analytics"] as const,
  funnel: () => ["analytics", "funnel"] as const,
} as const;

// ─── Brand Voices ──────────────────────────────────────────────────────────────

export type UseBrandVoicesResult = UseQueryResult<BrandVoiceListResponse, ApiError | Error>;

export function useBrandVoices(): UseBrandVoicesResult {
  const { getToken } = useAuth();
  return useQuery<BrandVoiceListResponse, ApiError | Error>({
    queryKey: contentKeys.voices(),
    queryFn: () => fetchBrandVoices({ getToken: () => getToken() }),
  });
}

export type UseCreateBrandVoiceResult = UseMutationResult<
  BrandVoiceResponse, ApiError | Error, CreateBrandVoiceRequest
>;

export function useCreateBrandVoice(): UseCreateBrandVoiceResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<BrandVoiceResponse, ApiError | Error, CreateBrandVoiceRequest>({
    mutationFn: (body) => createBrandVoice(body, { getToken: () => getToken() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contentKeys.voices() }),
  });
}

export type UseUpdateBrandVoiceResult = UseMutationResult<
  BrandVoiceResponse, ApiError | Error, { id: string; body: UpdateBrandVoiceRequest }
>;

export function useUpdateBrandVoice(): UseUpdateBrandVoiceResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<BrandVoiceResponse, ApiError | Error, { id: string; body: UpdateBrandVoiceRequest }>({
    mutationFn: ({ id, body }) => updateBrandVoice(id, body, { getToken: () => getToken() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contentKeys.voices() }),
  });
}

export type UseDeleteBrandVoiceResult = UseMutationResult<void, ApiError | Error, string>;

export function useDeleteBrandVoice(): UseDeleteBrandVoiceResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<void, ApiError | Error, string>({
    mutationFn: (id) => deleteBrandVoice(id, { getToken: () => getToken() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contentKeys.voices() }),
  });
}

// ─── Posts ────────────────────────────────────────────────────────────────────

export type UsePostsListResult = UseQueryResult<ContentPostListResponse, ApiError | Error>;

export function usePostsList(
  params: {
    status?: ContentPostResponse["status"];
    platform?: ContentPostResponse["platform"];
    limit?: number;
    cursor?: string;
  } = {},
): UsePostsListResult {
  const { getToken } = useAuth();
  return useQuery<ContentPostListResponse, ApiError | Error>({
    queryKey: contentKeys.posts(params),
    queryFn: () => fetchPosts(params, { getToken: () => getToken() }),
  });
}

export type UsePostResult = UseQueryResult<ContentPostResponse, ApiError | Error>;

export function usePost(id: string | undefined): UsePostResult {
  const { getToken } = useAuth();
  return useQuery<ContentPostResponse, ApiError | Error>({
    queryKey: contentKeys.post(id ?? ""),
    queryFn: () => {
      if (!id) throw new Error("id is required");
      return fetchPost(id, { getToken: () => getToken() });
    },
    enabled: Boolean(id),
  });
}

export type UseCreatePostResult = UseMutationResult<
  ContentPostResponse, ApiError | Error, CreateContentPostRequest
>;

export function useCreatePost(): UseCreatePostResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<ContentPostResponse, ApiError | Error, CreateContentPostRequest>({
    mutationFn: (body) => createPost(body, { getToken: () => getToken() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contentKeys.all }),
  });
}

export type UseGeneratePostResult = UseMutationResult<
  ContentPostResponse, ApiError | Error, GeneratePostRequest
>;

export function useGeneratePost(): UseGeneratePostResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<ContentPostResponse, ApiError | Error, GeneratePostRequest>({
    mutationFn: (body) => generatePost(body, { getToken: () => getToken() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contentKeys.all }),
  });
}

export type UseUpdatePostResult = UseMutationResult<
  ContentPostResponse, ApiError | Error, { id: string; body: UpdateContentPostRequest }
>;

export function useUpdatePost(): UseUpdatePostResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<ContentPostResponse, ApiError | Error, { id: string; body: UpdateContentPostRequest }>({
    mutationFn: ({ id, body }) => updatePost(id, body, { getToken: () => getToken() }),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: contentKeys.post(id) });
      void qc.invalidateQueries({ queryKey: contentKeys.all });
    },
  });
}

export type UseDeletePostResult = UseMutationResult<void, ApiError | Error, string>;

export function useDeletePost(): UseDeletePostResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<void, ApiError | Error, string>({
    mutationFn: (id) => deletePost(id, { getToken: () => getToken() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contentKeys.all }),
  });
}

export type UseSchedulePostResult = UseMutationResult<
  ContentPostResponse, ApiError | Error, { id: string; body: SchedulePostRequest }
>;

export function useSchedulePost(): UseSchedulePostResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<ContentPostResponse, ApiError | Error, { id: string; body: SchedulePostRequest }>({
    mutationFn: ({ id, body }) => schedulePost(id, body, { getToken: () => getToken() }),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: contentKeys.post(id) });
      void qc.invalidateQueries({ queryKey: contentKeys.all });
    },
  });
}

export type UsePublishPostResult = UseMutationResult<ContentPostResponse, ApiError | Error, string>;

export function usePublishPost(): UsePublishPostResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<ContentPostResponse, ApiError | Error, string>({
    mutationFn: (id) => publishPost(id, { getToken: () => getToken() }),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: contentKeys.post(id) });
      void qc.invalidateQueries({ queryKey: contentKeys.all });
    },
  });
}

// ─── Platform Connections ─────────────────────────────────────────────────────

export type UseConnectionsResult = UseQueryResult<PlatformConnectionListResponse, ApiError | Error>;

export function useConnections(): UseConnectionsResult {
  const { getToken } = useAuth();
  return useQuery<PlatformConnectionListResponse, ApiError | Error>({
    queryKey: contentKeys.connections(),
    queryFn: () => fetchConnections({ getToken: () => getToken() }),
  });
}

export type UseUpsertConnectionResult = UseMutationResult<
  PlatformConnectionResponse, ApiError | Error, { platform: string; body: UpsertPlatformConnectionRequest }
>;

export function useUpsertConnection(): UseUpsertConnectionResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<
    PlatformConnectionResponse,
    ApiError | Error,
    { platform: string; body: UpsertPlatformConnectionRequest }
  >({
    mutationFn: ({ platform, body }) => upsertConnection(platform, body, { getToken: () => getToken() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contentKeys.connections() }),
  });
}

export type UseDeleteConnectionResult = UseMutationResult<void, ApiError | Error, string>;

export function useDeleteConnection(): UseDeleteConnectionResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<void, ApiError | Error, string>({
    mutationFn: (platform) => deleteConnection(platform, { getToken: () => getToken() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: contentKeys.connections() }),
  });
}

// ─── Retry Post (C2 failed→scheduled) ────────────────────────────────────────

export type UseRetryPostResult = UseMutationResult<ContentPostResponse, ApiError | Error, string>;

export function useRetryPost(): UseRetryPostResult {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  return useMutation<ContentPostResponse, ApiError | Error, string>({
    mutationFn: (id) => retryPost(id, { getToken: () => getToken() }),
    onSuccess: (_, id) => {
      void qc.invalidateQueries({ queryKey: contentKeys.post(id) });
      void qc.invalidateQueries({ queryKey: contentKeys.all });
    },
  });
}

// ─── Analytics (C5) ──────────────────────────────────────────────────────────

export type UseContentAnalyticsResult = UseQueryResult<ContentAnalyticsResponse, ApiError | Error>;

export function useContentAnalytics(): UseContentAnalyticsResult {
  const { getToken } = useAuth();
  return useQuery<ContentAnalyticsResponse, ApiError | Error>({
    queryKey: contentKeys.analytics(),
    queryFn: () => fetchContentAnalytics({ getToken: () => getToken() }),
    staleTime: 60_000,
  });
}

// ─── Attribution Funnel (F1) ──────────────────────────────────────────────────

export type UseFunnelResult = UseQueryResult<FunnelResponse, ApiError | Error>;

export function useFunnel(): UseFunnelResult {
  const { getToken } = useAuth();
  return useQuery<FunnelResponse, ApiError | Error>({
    queryKey: contentKeys.funnel(),
    queryFn: () => fetchFunnel({ getToken: () => getToken() }),
    staleTime: 60_000,
  });
}

// ─── Image upload (R2) ───────────────────────────────────────────────────────

export type UseUploadContentImageResult = UseMutationResult<
  ContentImageUploadResponse,
  ApiError | Error,
  File
>;

export function useUploadContentImage(): UseUploadContentImageResult {
  const { getToken } = useAuth();
  return useMutation<ContentImageUploadResponse, ApiError | Error, File>({
    mutationFn: (file) => uploadContentImage(file, { getToken: () => getToken() }),
  });
}
